import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Radar, Truck, LayoutDashboard, ArrowRight, Search } from "lucide-react";
import { eyebrowFor } from "@/content/sections";
import { reveal } from "@/lib/motion";

const portals = [
  { icon: Radar, tag: "SHIPPERS", title: "Live Tracking", desc: "Track any load in real time with milestone alerts and proof of delivery.", cta: "Track a Shipment" },
  { icon: Truck, tag: "CARRIERS", title: "Carrier Portal", desc: "Browse loads, upload docs and get paid faster from one dashboard.", cta: "Carrier Login" },
  { icon: LayoutDashboard, tag: "INTERNAL", title: "Operations Console", desc: "The command center for our dispatch and brokerage operations team.", cta: "Ops Login" },
];

export const Portals = () => {
  const [track, setTrack] = useState("");
  const soon = (name) => toast.info(`${name} launching soon`, { description: "We're wiring up the portal — check back shortly." });

  return (
    <section id="portals" className="relative z-10 bg-ink py-28 lg:py-40" data-testid="portals">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="mb-16 max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-neon">{eyebrowFor("portals")}</span>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white md:text-6xl">
            One platform.<br />Total control.
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {portals.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.1 }}
                data-testid={`portal-${p.title.toLowerCase().replace(/\s/g, "-")}`}
                className="tracing-card flex flex-col justify-between border border-white/10 bg-[#0d0d0d] p-8 transition-colors duration-500 hover:bg-[#111]"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <Icon size={30} strokeWidth={1.4} className="text-neon" />
                    <span className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">{p.tag}</span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-semibold uppercase text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{p.desc}</p>
                </div>

                {i === 0 ? (
                  <form
                    onSubmit={(e) => { e.preventDefault(); soon("Live tracking"); }}
                    className="mt-8 flex items-center border border-white/15 bg-white/5"
                  >
                    <input
                      value={track}
                      onChange={(e) => setTrack(e.target.value)}
                      placeholder="Enter load #"
                      data-testid="tracking-input"
                      className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none"
                    />
                    <button type="submit" data-testid="tracking-submit" className="bg-neon p-3 text-black transition-colors hover:bg-white">
                      <Search size={16} />
                    </button>
                  </form>
                ) : (
                  <button
                    onClick={() => soon(p.title)}
                    data-testid={`portal-cta-${i}`}
                    className="group mt-8 flex items-center justify-between border border-white/15 px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-neon hover:text-neon"
                  >
                    {p.cta}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
